import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, FileText, ShoppingBag, Package, MessageCircle, User, LogOut, Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { customerApi } from '../lib/api';

const navItems = [
  { to: '/portal', label: 'Overview', icon: Home, end: true, testid: 'nav-overview' },
  { to: '/portal/invoices', label: 'Invoices', icon: FileText, testid: 'nav-invoices' },
  { to: '/portal/shop', label: 'Shop', icon: ShoppingBag, testid: 'nav-shop' },
  { to: '/portal/orders', label: 'My Orders', icon: Package, testid: 'nav-orders' },
  { to: '/portal/messages', label: 'Messages', icon: MessageCircle, testid: 'nav-messages' },
  { to: '/portal/profile', label: 'Profile', icon: User, testid: 'nav-profile' },
];

export default function CustomerLayout({ children, me }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const load = async () => {
      try {
        const r = await customerApi.get('/customer/messages/unread-count');
        setUnread(r.data.unread || 0);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const logout = async () => {
    try { await customerApi.post('/auth/logout'); } catch {}
    navigate('/customer/login');
  };

  return (
    <div className="min-h-screen bg-[#fafafa] pb-20 lg:pb-0">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#e7e7e7]">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 lg:px-8 h-28">
          <Link to="/portal" data-testid="portal-logo"><Logo size="lg" /></Link>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 text-sm">
              {me?.picture && <img src={me.picture} alt="" className="w-7 h-7 rounded-full" />}
              <span className="font-medium text-[#1f1f1f]">{me?.name}</span>
            </div>
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2" data-testid="mobile-menu-btn">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <button onClick={logout} className="hidden lg:flex items-center gap-2 text-sm text-[#6b7280] hover:text-[#0a0a0a] px-3 py-1.5" data-testid="customer-logout-btn">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
        {/* Desktop nav */}
        <nav className="hidden lg:flex max-w-6xl mx-auto px-8 gap-1 border-t border-[#f3f3f3]">
          {navItems.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive ? 'text-[#d92027] border-b-2 border-[#d92027]' : 'text-[#6b7280] hover:text-[#0a0a0a]'
                }`
              }
            >
              <Icon size={16} /> {label}
              {to === '/portal/messages' && unread > 0 && (
                <span className="bg-[#d92027] text-white text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/30" onClick={() => setOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-72 bg-white p-6 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <Logo size="md" />
              <button onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            {navItems.map(({ to, label, icon: Icon, end, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`mobile-${testid}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive ? 'bg-[#fde8e8] text-[#d92027]' : 'text-[#1f1f1f]'}`
                }
              >
                <Icon size={18} /> {label}
                {to === '/portal/messages' && unread > 0 && (
                  <span className="ml-auto bg-[#d92027] text-white text-[10px] font-bold px-1.5 rounded-full">{unread}</span>
                )}
              </NavLink>
            ))}
            <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-[#6b7280]" data-testid="mobile-logout-btn">
              <LogOut size={18} /> Sign out
            </button>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">{children}</main>

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#e7e7e7] safe-bottom">
        <div className="grid grid-cols-5">
          {navItems.slice(0, 5).map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`bottom-${testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2.5 gap-0.5 text-[11px] font-medium ${isActive ? 'text-[#d92027]' : 'text-[#6b7280]'}`
              }
            >
              <div className="relative">
                <Icon size={20} />
                {to === '/portal/messages' && unread > 0 && (
                  <span className="absolute -top-1 -right-2 bg-[#d92027] text-white text-[9px] font-bold px-1 rounded-full">{unread}</span>
                )}
              </div>
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
